// Copyright 2024, Imprivata, Inc.  All rights reserved.
// NOTE: This is temporary until feature flags have been properly implemented
import { _getEnv } from '@api/utils';
import featureFlags from './featureFlags.json';

export type FeatureFlags = typeof featureFlags;

export enum ENVIRONMENT {
  local = 1,
  dev = 2,
  int = 3,
  eph = 4,
  stg = 5,
  prod = 6,
  prod_eu = 7,
  prod_au = 8,
}

export const TldToEnv: Record<string, ENVIRONMENT> = {
  au: ENVIRONMENT.prod_au,
  eu: ENVIRONMENT.prod_eu,
};

export type FeatureEnvironment = keyof typeof ENVIRONMENT;

export type FeatureConfig = {
  PENDO_API_KEY: string;
  ENABLE_EPCS: boolean;
  ENABLE_LOGIN_ACTIVITY_REPORT: boolean;
  PASSWORD_EXPIRATION_ENABLED: boolean;
  PASSWORD_EXPIRATION_ENABLED_TENANTS?: string[];
  ENABLE_EMAIL_OTP?: boolean;
  ENABLE_EAMC_CHATBOT?: boolean;
  ENABLE_ADVANCED_SETTINGS_TAB?: boolean;
  ENABLE_TEST_ENTRA_ID?: boolean;
};

/**
 * @returns TRUE if your current env level (dev, int, prod) <= env level where the feature is enabled
 */
export function featureSupported(feature: keyof FeatureConfig): boolean {
  const env = _getEnv();
  let featureEnv: FeatureEnvironment = 'prod';

  if (ENVIRONMENT[env]) {
    featureEnv = ENVIRONMENT[env] as unknown as FeatureEnvironment;
  }
  const currentLevel = ENVIRONMENT[featureEnv].valueOf();
  const featureLevel = Object.keys(ENVIRONMENT)
    .filter(key => Number.isNaN(Number(key)))
    .find(key => {
      const envFeatures = featureFlags[key.toLowerCase() as keyof FeatureFlags];
      return (
        feature in envFeatures &&
        typeof envFeatures[feature as keyof typeof envFeatures] === 'boolean' &&
        envFeatures[feature as keyof typeof envFeatures]
      );
    });

  console.debug('[featureFlagUtils] feature flags: ', { feature, featureEnv, currentLevel, featureLevel });

  if (featureLevel) {
    return currentLevel <= ENVIRONMENT[featureLevel as keyof typeof ENVIRONMENT];
  }

  return false;
}

export function getFeatureFlags() {
  const currentEnv = _getEnv();
  const envName = ENVIRONMENT[currentEnv] as keyof FeatureFlags;
  let features = featureFlags['prod' as keyof FeatureFlags];

  if (envName && envName in featureFlags) {
      features = featureFlags[envName];
  }

  console.debug('[featureFlagUtils] feature flags: ', { currentEnv, envName,features });
  return features;
}

export function isPasswordExpirationEnabledForTenant(tenantId: string): boolean {
  const features = getFeatureFlags();
  if (features.PASSWORD_EXPIRATION_ENABLED) {
    return true;
  }
  const enabledTenants = features.PASSWORD_EXPIRATION_ENABLED_TENANTS || [];
  return enabledTenants.includes(tenantId);
}
